import { Alert, Box, Form, FormField, FormLabel, Input, Loader } from '@plusplusminus/plusplusdash'
import { navigate, RouteComponentProps } from '@reach/router'
import { RichTextEditor } from 'components/RichTextEditor'
import { DeleteModal } from 'components/Modals/Delete'
import { useSizingGuideQuery, useUpdateSizingGuideMutation, useDeleteSizingGuideMutation } from 'hooks/sizingGuides'
import { useState, useEffect } from 'react'
import { useForm } from 'react-hook-form'
import toast from 'react-hot-toast'
import Button from 'components/Button'

interface RouteProps {
  guideId?: string
}

interface FormValues {
  name: string
}

export const SizingGuide: React.FC<RouteComponentProps<RouteProps>> = ({ guideId }) => {
  const { data, loading, error, refetch } = useSizingGuideQuery(guideId as string)
  const [content, setContent] = useState('')
  const [isDeleteOpen, setIsDeleteOpen] = useState(false)
  const { register, handleSubmit, reset } = useForm<FormValues>()

  useEffect(() => {
    if (data?.sizingGuide) {
      reset({ name: data.sizingGuide.name })
      setContent(data.sizingGuide.content ?? '')
    }
  }, [data])

  const { updateSizingGuide, loading: loadingUpdate } = useUpdateSizingGuideMutation({
    onCompleted: () => {
      toast.success('Sizing guide updated')
      refetch()
    },
    onError: () => toast.error('Could not update sizing guide')
  })

  const { deleteSizingGuide, loading: loadingDelete } = useDeleteSizingGuideMutation({
    onCompleted: () => {
      toast.success('Sizing guide deleted')
      navigate('/sizing-guides', { state: { refetch: true } })
    },
    onError: () => toast.error('Could not delete sizing guide')
  })

  if (error) {
    return (
      <Alert type="error">
        <Alert.Heading>Error fetching sizing guide</Alert.Heading>
      </Alert>
    )
  }

  if (!data || loading) return <Loader isActive />

  const onSubmit = (formData: FormValues) => {
    updateSizingGuide(guideId as string, { name: formData.name, content })
  }

  return (
    <Box>
      <DeleteModal
        headline="Delete Sizing Guide"
        description="Are you sure you want to delete this sizing guide? Brands using it will lose their guide."
        isModalOpen={isDeleteOpen}
        isLoading={loadingDelete}
        onClose={() => setIsDeleteOpen(false)}
        onDelete={() => deleteSizingGuide(guideId as string)}
      />
      <Box className="flex justify-between p-5 border-b bg-white border-gray-300 items-center">
        <Box>
          <h3 className="text-lg leading-6 font-medium text-gray-900">{data.sizingGuide.name}</h3>
          <p className="mt-1 max-w-2xl text-sm text-gray-500">Edit sizing guide content</p>
        </Box>
        <Button variant="primary" colorScheme="red" type="button" size="sm" onClick={() => setIsDeleteOpen(true)}>
          Delete
        </Button>
      </Box>
      <Box className="p-5">
        <Form onSubmit={handleSubmit(onSubmit)}>
          <div className="mb-4 flex flex-col gap-4">
            <FormField direction="column">
              <FormLabel htmlFor="name">Guide Name *</FormLabel>
              <Input
                id="name"
                name="name"
                as="input"
                variant="standard"
                width="full"
                ref={register({ required: true })}
              />
            </FormField>
            <FormField direction="column">
              <FormLabel>Content</FormLabel>
              <RichTextEditor value={content} onChange={setContent} />
            </FormField>
          </div>
          <Button
            variant="primary"
            colorScheme="green"
            type="submit"
            className="px-8"
            disabled={loadingUpdate}
            isLoading={loadingUpdate}
          >
            Save
          </Button>
        </Form>
      </Box>
    </Box>
  )
}
